import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, FileCheck, Home,
  FileText, Inbox, FileOutput, Receipt, CreditCard, Wallet,
  CheckSquare, Users2, Calculator, BookOpenCheck, PieChart, Sparkles, Settings,
  ScrollText, Globe2, Building2, FolderArchive, GitCompareArrows, AlarmClock, Gauge,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../lib/cn';
import { useWorkspace } from '../hooks/useWorkspace';
import { useWorkspaceLiveEvents } from '../hooks/useWorkspaceLiveEvents';
import { useLastWorkspace } from '../hooks/useLastWorkspace';
import { useWorkspaceStore } from '../stores/workspaceStore';
import { LanguagePicker } from './LanguagePicker';
import { UserMenu } from './UserMenu';
import { NotificationBell } from './NotificationBell';
import { SignalsBell } from './SignalsBell';
import { HeaderBreadcrumb } from './HeaderBreadcrumb';
import { AiCostMeter } from './AiCostMeter';
import { EntitySwitcher } from './EntitySwitcher';
import { KeyboardShortcutsProvider } from './KeyboardShortcutsProvider';
import { UnverifiedEmailBanner } from './UnverifiedEmailBanner';
import { PlyEZMark } from './BrandLogo';

interface AppShellProps {
  children: ReactNode;
}

type NavItem = { to: (wsId: string) => string; icon: typeof Inbox; key: string };
// Grouped information architecture. `section` is null for top-level
// ungrouped items (rendered before any labeled group); otherwise it is an
// `appNav.sections.*` i18n key. Section headers are hidden in collapsed
// (icon-only) rail mode. ALL existing routes are preserved.
type NavGroup = { section: string | null; items: NavItem[] };

const NAV_GROUPS: NavGroup[] = [
  {
    section: null,
    items: [
      { to: (id) => `/app/workspace/${id}/home`, icon: Home, key: 'home' },
      { to: (id) => `/app/workspace/${id}/inbox`, icon: Inbox, key: 'inbox' },
      { to: (id) => `/app/workspace/${id}/approvals`, icon: CheckSquare, key: 'approvals' },
    ],
  },
  {
    section: 'payables',
    items: [
      { to: (id) => `/app/workspace/${id}/ap-invoices`, icon: FileText, key: 'ap' },
      { to: (id) => `/app/workspace/${id}/matching`, icon: GitCompareArrows, key: 'matching' },
      { to: (id) => `/app/workspace/${id}/payments`, icon: Wallet, key: 'payments' },
      { to: (id) => `/app/workspace/${id}/vendors`, icon: Users2, key: 'vendors' },
      { to: (id) => `/app/workspace/${id}/cards`, icon: CreditCard, key: 'cards' },
    ],
  },
  {
    section: 'receivables',
    items: [
      { to: (id) => `/app/workspace/${id}/ar-invoices`, icon: FileOutput, key: 'ar' },
      { to: (id) => `/app/workspace/${id}/collections`, icon: AlarmClock, key: 'collections' },
    ],
  },
  {
    section: 'expenses',
    items: [
      { to: (id) => `/app/workspace/${id}/expenses`, icon: Receipt, key: 'expenses' },
    ],
  },
  {
    section: 'accounting',
    items: [
      { to: (id) => `/app/workspace/${id}/closing`, icon: Calculator, key: 'closing' },
      { to: (id) => `/app/workspace/${id}/close-packet`, icon: FileCheck, key: 'closePacket' },
      { to: (id) => `/app/workspace/${id}/ledger`, icon: BookOpenCheck, key: 'ledger' },
      { to: (id) => `/app/workspace/${id}/budgets`, icon: Gauge, key: 'budgets' },
      { to: (id) => `/app/workspace/${id}/tax-us`, icon: FileText, key: 'taxUs' },
      { to: (id) => `/app/workspace/${id}/reports`, icon: PieChart, key: 'reports' },
    ],
  },
  {
    section: 'compliance',
    items: [
      { to: (id) => `/app/workspace/${id}/digital-records`, icon: FolderArchive, key: 'digitalRecords' },
      { to: (id) => `/app/workspace/${id}/peppol`, icon: Globe2, key: 'peppol' },
    ],
  },
  {
    section: 'bank',
    items: [
      { to: (id) => `/app/workspace/${id}/bank-feed`, icon: Building2, key: 'bankFeed' },
    ],
  },
  {
    section: 'ai',
    items: [
      { to: (id) => `/app/workspace/${id}/agents`, icon: Sparkles, key: 'agents' },
    ],
  },
  {
    section: null,
    items: [
      { to: (id) => `/app/workspace/${id}/audit`, icon: ScrollText, key: 'audit' },
      { to: (id) => `/app/workspace/${id}/settings`, icon: Settings, key: 'settings' },
    ],
  },
];

const COLLAPSE_KEY = 'plyez.sidebarCollapsed';

export function AppShell({ children }: AppShellProps) {
  const { wsId: routeWsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const setLastWorkspaceId = useWorkspaceStore((s) => s.setLastWorkspaceId);
  // Persist the workspace the user is *actually* inside (from the URL).
  // The fallback below must never overwrite this real last-active id.
  useEffect(() => {
    if (routeWsId) setLastWorkspaceId(routeWsId);
  }, [routeWsId, setLastWorkspaceId]);

  // Account-level pages (e.g., /app/profile) don't carry a wsId in the
  // URL but should still show the workspace sidebar so users can jump
  // back into work. Prefer the last-active workspace, then the first.
  const { resolvedWorkspaceId, appHomePath } = useLastWorkspace();
  const wsId = routeWsId ?? resolvedWorkspaceId ?? undefined;
  const { data: workspace } = useWorkspace(wsId);

  // Subscribe to workspace-scoped realtime events (new AP invoices, OCR
  // completion, approval state changes) over the shared socket so lists
  // and counts refresh live. No-op until a workspace is resolved.
  useWorkspaceLiveEvents(wsId);

  // Sidebar collapse — spec §6.3 calls for "icons + labels at >1200px,
  // icons-only collapsed". Persisted per-user via localStorage; on first
  // load we auto-collapse when the viewport is narrower than 1200px.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem(COLLAPSE_KEY);
    if (stored != null) return stored === '1';
    return window.innerWidth < 1200;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    }
  }, [collapsed]);

  return (
    <KeyboardShortcutsProvider>
      <div className="flex min-h-screen bg-ink-50 text-ink-900 dark:bg-ink-950 dark:text-ink-100">
        <a
          href="#app-main"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:dark:bg-ink-900 focus:px-3 focus:py-2 focus:rounded focus:border focus:border-ink-300"
        >
          {t('a11y.skipToContent')}
        </a>

        <aside
          className={cn(
            'shrink-0 border-r border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 flex flex-col transition-[width] duration-150',
            collapsed ? 'w-14' : 'w-60',
          )}
        >
          <Link
            to={appHomePath}
            aria-label={t('brand')}
            className={cn(
              'flex items-center gap-2 border-b border-ink-200 dark:border-ink-800 h-[60px] hover:opacity-90 transition-opacity',
              collapsed ? 'justify-center' : 'px-5',
            )}
          >
            <PlyEZMark />
            {!collapsed && <span className="font-display text-lg font-semibold">{t('brand')}</span>}
          </Link>

          <nav className="flex-1 overflow-y-auto py-2" aria-label={t('appNav.aria')}>
            {wsId && NAV_GROUPS.map((group, gi) => (
              <div key={group.section ?? `top-${gi}`} className={gi > 0 ? 'mt-2' : undefined}>
                {group.section && !collapsed && (
                  <div className="px-5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-400 dark:text-ink-500">
                    {t(`appNav.sections.${group.section}`)}
                  </div>
                )}
                {group.section && collapsed && (
                  <div className="mx-3 my-1.5 border-t border-ink-100 dark:border-ink-800" aria-hidden />
                )}
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.key}
                      to={item.to(wsId)}
                      title={collapsed ? t(`appNav.${item.key}`) : undefined}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 py-2 text-sm transition-colors',
                          collapsed ? 'justify-center px-0' : 'px-5',
                          isActive
                            ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/40 dark:text-accent-100 border-r-2 border-accent-500'
                            : 'text-ink-600 hover:bg-ink-50 hover:text-ink-900 dark:text-ink-300 dark:hover:bg-ink-800',
                        )
                      }
                    >
                      <Icon size={16} aria-hidden />
                      {!collapsed && <span>{t(`appNav.${item.key}`)}</span>}
                    </NavLink>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className={cn(
            'border-t border-ink-200 dark:border-ink-800 text-xs text-ink-500',
            collapsed ? 'py-3 flex flex-col items-center gap-2' : 'px-5 py-4 space-y-2',
          )}>
            {!collapsed && workspace ? (
              <div className="space-y-1">
                <div className="font-medium text-ink-700 dark:text-ink-200">{workspace.name}</div>
                <div className="font-mono text-[10px] truncate">{workspace.inbox_alias}@in.plyez.com</div>
              </div>
            ) : null}
            <button
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? t('topbar.expandSidebar') : t('topbar.collapseSidebar')}
              className="p-1.5 rounded-md hover:bg-ink-100 dark:hover:bg-ink-800 text-ink-500"
            >
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
          </div>
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 px-4 lg:px-6 border-b border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 flex items-center gap-3">
            <div className="flex-1 min-w-0 flex items-center gap-3">
              <HeaderBreadcrumb />
            </div>
            <div className="flex items-center gap-2">
              {wsId && <EntitySwitcher workspaceId={wsId} />}
              {workspace?.base_currency && (
                <span
                  className="hidden md:inline-flex items-center h-8 px-2 rounded-lg border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-900 text-xs num text-ink-600 dark:text-ink-300"
                  title={t('topbar.currencyHint')}
                >
                  {workspace.base_currency}
                </span>
              )}
              {wsId && <AiCostMeter workspaceId={wsId} />}
              <LanguagePicker compact />
              {wsId && <SignalsBell />}
              <NotificationBell />
              <UserMenu />
            </div>
          </header>
          <UnverifiedEmailBanner />
          <main id="app-main" className="flex-1 overflow-auto">{children}</main>
        </div>
      </div>
    </KeyboardShortcutsProvider>
  );
}
